import React, {useEffect, useMemo} from 'react';

import {UserStatusEnum, UserTypeEnum} from 'src/api/ProactApiClient';
import {ProactForm, ProactFormGrid} from 'src/app/forms/engine/ProactForm';
import {useProactForm} from 'src/app/forms/engine/useProactForm';
import {FormViewComponentV2} from 'src/app/forms/FormViewComponent/FormViewComponentV2';
import {TableBodyRender} from 'src/design/molecules/TableBodyRender';
import {useGetFormDefinitionSelectOptionsQuery} from 'src/redux/queries/formDefinition';
import {useGetPersonnelSelectOptionsQuery} from 'src/redux/queries/personnel';
import {useGetUserGroupSelectOptionsQuery} from 'src/redux/queries/userGroup';
import useSkipTokensByFeature from 'src/shared/hooks/useSkipTokensByFeature';
import type {IFormDataV2} from 'src/types/FormV2';
import {DemonstrationFormMeta} from '../../../../../app/training/courseFormMeta';

export interface DemonstrationFormProps {
  data: IFormDataV2;
  mode: 'edit' | 'view';
  onFormDataChange?: (data: IFormDataV2) => void;
  /** Wave 6: true once a Skill is linked — hides the legacy Form field. */
  hideFormSelect?: boolean;
}
export const DemonstrationForm: React.FC<DemonstrationFormProps> = ({
  data,
  mode,
  onFormDataChange,
  hideFormSelect = false,
}) => {
  const {skipForms} = useSkipTokensByFeature();
  const {
    data: personnel,
    isFetching: isPersonnelFetching,
    isLoading: isPersonnelLoading,
  } = useGetPersonnelSelectOptionsQuery({
    start: 0,
    length: 10000,
    active: [UserStatusEnum.Active],
    userTypes: [UserTypeEnum.Contractor, UserTypeEnum.Employee],
    superviserOnly: false,
  });
  const {
    data: userGroups,
    isFetching: isUserGroupsFetching,
    isLoading: isUserGroupsLoading,
  } = useGetUserGroupSelectOptionsQuery({
    start: 0,
    length: 10000,
    active: true,
  });
  const {
    data: forms,
    isFetching: isFormsFetching,
    isLoading: isFormsLoading,
  } = useGetFormDefinitionSelectOptionsQuery(
    {start: 0, length: 10000, includeSubforms: false},
    {skip: skipForms}
  );

  const isLoading =
    isPersonnelFetching ||
    isPersonnelLoading ||
    isUserGroupsFetching ||
    isUserGroupsLoading ||
    isFormsFetching ||
    isFormsLoading;

  const formMeta = useMemo(
    () =>
      DemonstrationFormMeta(
        forms?.data ?? [],
        personnel?.data ?? [],
        userGroups?.data ?? [],
        hideFormSelect
      ),
    [forms, personnel, userGroups, hideFormSelect]
  );

  const form = useProactForm({formMeta, initialData: data ?? {}});
  const {methods, validateForm} = form;

  useEffect(() => {
    if (mode !== 'edit' || isLoading) {
      return;
    }
    void validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, isLoading]);

  useEffect(() => {
    if (mode !== 'edit') {
      return;
    }
    const subscription = methods.watch((values, {name}) => {
      if (!name) {
        return;
      }
      onFormDataChange?.({...values});
      void validateForm();
    });
    return () => subscription.unsubscribe();
  }, [mode, methods, onFormDataChange, validateForm]);

  return isLoading === false ? (
    mode === 'edit' ? (
      <ProactForm form={form}>
        <ProactFormGrid />
      </ProactForm>
    ) : (
      <FormViewComponentV2 formData={data ?? {}} formMeta={formMeta} />
    )
  ) : (
    <TableBodyRender isLoading={isLoading} />
  );
};
